import { memo } from 'react';
import { Handle, Position, NodeProps } from '@xyflow/react';
import { SimpleGraphNode, SimpleNodeData } from './types';

// SaaS app logos/icons mapping
const saasLogos: Record<string, string> = {
  github: 'https://github.githubassets.com/images/modules/logos_page/GitHub-Mark.png',
  slack: 'https://a.slack-edge.com/80588/marketing/img/icons/icon_slack_hash_colored.png',
  salesforce: 'https://www.salesforce.com/news/wp-content/uploads/sites/3/2021/05/Salesforce-logo.jpg',
  jira: 'https://wac-cdn.atlassian.com/dam/jcr:b544631f-b225-431e-9a0e-742a67bb3336/Jira%20Software@2x-blue.png',
  datadog: 'https://imgix.datadoghq.com/img/dd_logo_n_70x75.png',
  pagerduty: 'https://www.pagerduty.com/wp-content/uploads/2020/07/PagerDuty-GreenMark-RGB.png',
};

const borderColors: Record<string, string> = {
  high: '#ef4444',
  medium: '#f59e0b',
  low: '#10b981',
};

export const SaaSNode = memo(({ data, selected }: NodeProps<SimpleGraphNode>) => {
  const nodeData = data as SimpleNodeData & { saasName?: string; risk?: string };
  const risk = nodeData.risk || 'low';
  const borderColor = borderColors[risk] || '#6b7280';
  const saasName = nodeData.saasName?.toLowerCase() || nodeData.label.toLowerCase();
  const logoUrl = saasLogos[saasName];

  const nodeSize = 80;

  return (
    <div className="relative flex flex-col items-center cursor-pointer transition-transform duration-200">
      {/* Main circle */}
      <div
        className={`rounded-full border-3 flex items-center justify-center transition-all bg-background overflow-hidden ${
          selected ? 'ring-2 ring-sky-500 shadow-[0_0_15px_rgba(59,130,246,0.4)]' : ''
        }`}
        style={{
          width: nodeSize,
          height: nodeSize,
          borderColor,
          borderWidth: 3,
        }}
      >
        {/* Invisible handles */}
        <Handle
          type="target"
          position={Position.Left}
          className="!opacity-0 !w-0 !h-0 !border-0"
          style={{ left: '50%', top: '50%' }}
        />
        <Handle
          type="source"
          position={Position.Right}
          className="!opacity-0 !w-0 !h-0 !border-0"
          style={{ right: '50%', top: '50%' }}
        />

        {/* Logo or fallback */}
        {logoUrl ? (
          <img 
            src={logoUrl} 
            alt={nodeData.label}
            className="w-12 h-12 object-contain"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        ) : (
          <span className="text-lg font-bold text-foreground">
            {nodeData.label.charAt(0).toUpperCase()}
          </span>
        )}
      </div>

      {/* Label below */}
      <div className="mt-2 max-w-[100px] text-center">
        <span className="text-[12px] font-semibold text-foreground block truncate">
          {nodeData.label}
        </span>
        <span className={`text-[10px] font-medium ${
          risk === 'high' ? 'text-primary' :
          risk === 'medium' ? 'text-primary' : 'text-success'
        }`}>
          {risk} risk
        </span>
      </div>
    </div>
  );
});

SaaSNode.displayName = 'SaaSNode';
